import React from 'react';
import { Bot, Mail, Phone, Globe, MapPin } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/utils';

interface PrintableDocumentProps {
  type: 'invoice' | 'quote' | 'payment' | 'expense' | 'client';
  data: any;
  businessInfo?: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website?: string;
    logo?: string;
  };
  children?: React.ReactNode;
}

export const PrintableDocument: React.FC<PrintableDocumentProps> = ({
  type,
  data,
  businessInfo = {
    name: 'AI CRM Business Solutions',
    address: '123 Business Street, Tech City, TC 12345',
    phone: '+1 (555) 123-4567',
    email: 'contact@aicrm.com',
    website: 'www.aicrm.com'
  },
  children
}) => {
  const getDocumentTitle = () => {
    switch (type) {
      case 'invoice':
        return 'INVOICE';
      case 'quote':
        return 'QUOTATION';
      case 'payment':
        return 'PAYMENT RECEIPT';
      case 'expense':
        return 'EXPENSE REPORT';
      case 'client':
        return 'CLIENT INFORMATION';
      default:
        return 'DOCUMENT';
    }
  };

  const getDocumentNumber = () => {
    switch (type) {
      case 'invoice':
        return data.invoice_number;
      case 'quote':
        return data.quote_number;
      case 'payment':
        return `PAY-${data.id?.slice(-8)}`;
      case 'expense':
        return `EXP-${data.id?.slice(-8)}`;
      case 'client':
        return `CLI-${data.id?.slice(-8)}`;
      default:
        return '';
    }
  };

  return (
    <div className="print-document bg-white min-h-screen">
      {/* Letterhead Header */}
      <div className="letterhead-header border-b-4 border-primary-600 pb-6 mb-8">
        <div className="flex items-start justify-between">
          {/* Company Logo and Info */}
          <div className="flex items-start space-x-6">
            <div className="company-logo">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Bot className="w-12 h-12 text-white" />
              </div>
            </div>
            <div className="company-info">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{businessInfo.name}</h1>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{businessInfo.address}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{businessInfo.phone}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{businessInfo.email}</span>
                </div>
                {businessInfo.website && (
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <span>{businessInfo.website}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Document Title and Number */}
          <div className="text-right">
            <h2 className="text-4xl font-bold text-primary-600 mb-2">{getDocumentTitle()}</h2>
            <div className="bg-primary-50 px-4 py-2 rounded-lg border border-primary-200">
              <p className="text-sm text-primary-600 font-medium">Document #</p>
              <p className="text-lg font-bold text-primary-800">{getDocumentNumber()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Document Content */}
      <div className="document-content">
        {children}
      </div>

      {/* Footer */}
      <div className="letterhead-footer mt-12 pt-6 border-t border-gray-200">
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">Thank you for your business!</p>
          <p>{businessInfo.name} • {businessInfo.phone} • {businessInfo.email}</p>
          {businessInfo.website && <p>{businessInfo.website}</p>}
        </div>
      </div>
    </div>
  );
};

// Print Styles Component
export const PrintStyles: React.FC = () => (
  <style jsx global>{`
    @media print {
      @page {
        size: A4;
        margin: 0.5in;
      }
      
      body {
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
      
      .print-document {
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        line-height: 1.4;
        color: #1f2937;
      }
      
      .letterhead-header {
        border-bottom: 4px solid #4f46e5 !important;
        padding-bottom: 24px;
        margin-bottom: 32px;
      }
      
      .company-logo {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #4f46e5 0%, #14b8a6 100%) !important;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      }
      
      .document-table {
        width: 100%;
        border-collapse: collapse;
        margin: 16px 0;
      }
      
      .document-table th,
      .document-table td {
        padding: 12px;
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
      }
      
      .document-table th {
        background-color: #f9fafb !important;
        font-weight: 600;
        color: #374151;
      }
      
      .total-section {
        background-color: #f9fafb !important;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 16px;
        margin-top: 24px;
      }
      
      .letterhead-footer {
        margin-top: 48px;
        padding-top: 24px;
        border-top: 1px solid #d1d5db;
        text-align: center;
        color: #6b7280;
      }
      
      /* Hide non-printable elements */
      .no-print,
      .sidebar,
      .header,
      button:not(.print-button),
      .floating-button {
        display: none !important;
      }
    }
  `}</style>
);